import React, { useState, useEffect, useContext, useCallback } from 'react';
import { fmtDate as _fmtDate } from '@/lib/dates';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { MoreHorizontal, Pencil, Power, Trash2, Search, Plus, History, Upload, X, ShieldAlert } from 'lucide-react';
import ProviderForm from './ProviderForm';
import ProviderDetailCard from './ProviderDetailCard';
import ImportVendorsDialog from './ImportVendorsDialog';
import BackfillReviewDialog from './BackfillReviewDialog';
import ProviderCoverageMap from './ProviderCoverageMap';
import ServiceSlices from './ServiceSlices';
import { DIVISIONS, deriveDivisions, insuranceFlag, isAdminProvider, isFieldProvider } from '@/lib/providerDivisions';
import { isGlobalAdmin } from '@/lib/workspaces';
import { StatusReasonDialog, PurgeEntityDialog, HistoryDialog } from './EntityDialogs';
import { MobileRecordCard, MobileRecordList } from '@/components/common/mobile/MobileRecordCard';

const StatusPill = ({ status }) => {
  const active = String(status || 'active').toLowerCase() === 'active';
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${active ? 'bg-blue-100 text-blue-700' : 'bg-[#FBF3E4] text-[#C8641E]'}`} title="Operational status (separate from qualification)">
      {active ? 'Active' : 'Inactive'}
    </span>
  );
};

const ProviderManager = () => {
  const { token, user } = useContext(AuthContext);
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
  const canPurge = isGlobalAdmin(user);   // Global Administrator only (owner or admin.controls)
  const [items, setItems] = useState([]);
  const [loadError, setLoadError] = useState(false);
  const [insights, setInsights] = useState({});
  const [search, setSearch] = useState('');
  const [formProvider, setFormProvider] = useState(undefined);
  const [statusTarget, setStatusTarget] = useState(null);
  const [purgeTarget, setPurgeTarget] = useState(null);
  const [historyTarget, setHistoryTarget] = useState(null);
  const [detailProvider, setDetailProvider] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [showBackfill, setShowBackfill] = useState(false);
  const [fQual, setFQual] = useState('');       // '' | registered_pending | qualified
  const [fStatus, setFStatus] = useState('');   // '' | active | inactive
  const [fDiv, setFDiv] = useState('');         // division lens — drives the map coloring only
  const [fState, setFState] = useState('');
  const [fCounty, setFCounty] = useState('');
  const [fSpecialty, setFSpecialty] = useState('');
  const [fContact, setFContact] = useState('');
  const [fInsurance, setFInsurance] = useState('');  // '' | expired | expiring_soon | missing
  const [fClass, setFClass] = useState('');  // '' | field | admin

  const load = useCallback(() => {
    axios.get(`${API}/service-providers`, authHeaders)
      .then((r) => { setItems(r.data || []); setLoadError(false); })
      .catch(() => setLoadError(true));
    axios.get(`${API}/service-providers/insights`, authHeaders).then((r) => setInsights(r.data || {})).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const specialtyList = (p) => (p.specialty_trades && p.specialty_trades.length)
    ? p.specialty_trades : (p.specialties ? [p.specialties] : []);

  // Header filters apply across the ENTIRE result set (items), not just the visible page.
  const filtered = items.filter((p) => {
    if (fQual && (p.qualification_status || 'registered_pending') !== fQual) return false;
    if (fStatus && (p.status || 'active') !== fStatus) return false;
    if (fState && !(p.service_states || []).map((s) => s.toUpperCase()).includes(fState.toUpperCase())) return false;
    if (fCounty && !([...(p.service_counties || []), ...(p.partial_counties || [])]).some((c) => c.toLowerCase().includes(fCounty.toLowerCase()))) return false;
    if (fSpecialty) {
      const q = fSpecialty.toLowerCase();
      if (!specialtyList(p).some((s) => (s || '').toLowerCase().includes(q))) return false;
    }
    if (fContact && !`${p.contact_name || ''} ${p.contact_email || ''} ${p.contact_phone || ''}`.toLowerCase().includes(fContact.toLowerCase())) return false;
    if (fInsurance) {
      const fl = insuranceFlag(p);
      const key = !fl ? '' : fl.label.includes('expired') ? 'expired' : fl.label.includes('expiring') ? 'expiring_soon' : 'missing';
      if (key !== fInsurance) return false;
    }
    if (fClass === 'admin' && !isAdminProvider(p)) return false;
    if (fClass === 'field' && !isFieldProvider(p)) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return `${p.company_name || ''} ${p.qsp_number || ''} ${(p.trade_types || []).join(' ')} ${p.contact_name || ''} ${(p.service_states || []).join(' ')}`.toLowerCase().includes(q);
  });

  const activeFilterCount = [fQual, fStatus, fState, fCounty, fSpecialty, fContact, fInsurance, fClass, search].filter(Boolean).length;
  const clearAll = () => { setFQual(''); setFStatus(''); setFState(''); setFCounty(''); setFSpecialty(''); setFContact(''); setFInsurance(''); setFClass(''); setSearch(''); };

  const allCounties = React.useMemo(() => {
    const s = new Set();
    items.forEach((p) => [...(p.service_counties || []), ...(p.partial_counties || [])].forEach((c) => s.add(c)));
    return Array.from(s).sort();
  }, [items]);

  return (
    <div>
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="min-w-0">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#8FC93A]">Administration</div>
          <h1 className="text-2xl font-extrabold text-[#0E2A47] sm:text-3xl">Service Providers</h1>
          <p className="text-sm text-slate-500 mt-1">Shared vendor &amp; subcontractor directory — managed here, assignable to work orders across ROUT dispatch.</p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:shrink-0 sm:items-center">
          <button onClick={() => setShowBackfill(true)} data-testid="backfill-review-btn"
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-4 py-2 text-sm font-semibold text-[#C8641E] hover:bg-[#C8641E]/5 max-sm:min-h-[44px] max-sm:w-full max-sm:justify-center">
            <ShieldAlert size={16} /> Backfill Review
          </button>
          <button onClick={() => setShowImport(true)} data-testid="import-vendors-btn"
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-4 py-2 text-sm font-semibold text-[#0E2A47] hover:bg-slate-50 max-sm:min-h-[44px] max-sm:w-full max-sm:justify-center">
            <Upload size={16} /> Import Vendors
          </button>
          <button onClick={() => setFormProvider(null)} data-testid="add-provider-btn"
            className="inline-flex items-center gap-1.5 rounded-md bg-[#0E2A47] px-4 py-2 text-sm font-semibold text-white hover:bg-[#16324F] max-sm:min-h-[44px] max-sm:w-full max-sm:justify-center">
            <Plus size={16} /> Add Service Provider
          </button>
        </div>
      </div>

      <div className="mb-6" data-testid="coverage-summary">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-bold text-[#0E2A47]">Florida coverage overview</h2>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400">Map lens:</span>
            <select value={fDiv} onChange={(e) => setFDiv(e.target.value)} data-testid="map-division-lens"
              className="rounded-md border border-slate-200 px-2.5 py-1.5 text-xs text-slate-700">
              <option value="">All service divisions</option>
              {DIVISIONS.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
        </div>
        <ProviderCoverageMap providers={filtered} division={fDiv} onOpenProvider={(p) => setDetailProvider(p)} />
      </div>

      <div className="mb-3 flex items-center justify-between" data-testid="provider-filters">
        <p className="text-xs text-slate-500">
          Showing <span className="font-semibold text-[#0E2A47]" data-testid="provider-result-count">{filtered.length}</span> of {items.length} providers
          {activeFilterCount > 0 && <span className="ml-1 text-slate-400">· {activeFilterCount} filter{activeFilterCount > 1 ? 's' : ''} active</span>}
        </p>
        {activeFilterCount > 0 && (
          <button onClick={clearAll} data-testid="filter-clear-all"
            className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2.5 py-1 text-xs font-semibold text-[#0E2A47] hover:bg-slate-50">
            <X size={12} /> Clear all filters
          </button>
        )}
      </div>

      <Card className="hidden lg:block">
        <CardContent className="p-0">
          <Table data-testid="providers-table" containerClassName="max-h-[70vh] overflow-auto rounded-md">
            <TableHeader>
              <TableRow className="bg-gray-50 align-top">
                <TableHead className="sticky top-0 z-10 bg-gray-50">
                  <div className="font-semibold text-slate-600">Company</div>
                  <div className="mt-1 flex flex-col gap-1">
                    <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name…"
                      data-testid="filter-company" className={`w-full rounded border px-2 py-1 text-xs font-normal ${search ? 'border-[#0E2A47] bg-[#0E2A47]/5' : 'border-slate-200'}`} />
                    <select value={fQual} onChange={(e) => setFQual(e.target.value)} data-testid="filter-qualification"
                      className={`w-full rounded border px-1.5 py-1 text-xs font-normal ${fQual ? 'border-[#0E2A47] bg-[#0E2A47]/5' : 'border-slate-200'}`}>
                      <option value="">All qualification</option>
                      <option value="qualified">Qualified</option>
                      <option value="registered_pending">Registered / Pending</option>
                    </select>
                  </div>
                </TableHead>
                <TableHead className="sticky top-0 z-10 bg-gray-50">
                  <div className="font-semibold text-slate-600">QSP #</div>
                  <div className="mt-1 text-[10px] font-normal text-slate-400">qualified only</div>
                </TableHead>
                <TableHead className="sticky top-0 z-10 bg-gray-50 hidden md:table-cell">
                  <div className="font-semibold text-slate-600">Specialty Trades</div>
                  <div className="mt-1 flex flex-col gap-1">
                    <input value={fSpecialty} onChange={(e) => setFSpecialty(e.target.value)} placeholder="Any specialty…"
                      data-testid="filter-specialty" className={`w-full rounded border px-2 py-1 text-xs font-normal ${fSpecialty ? 'border-[#0E2A47] bg-[#0E2A47]/5' : 'border-slate-200'}`} />
                    <select value={fClass} onChange={(e) => setFClass(e.target.value)} data-testid="filter-classification"
                      className={`w-full rounded border px-1.5 py-1 text-xs font-normal ${fClass ? 'border-[#0E2A47] bg-[#0E2A47]/5' : 'border-slate-200'}`}>
                      <option value="">All classifications</option>
                      <option value="field">Field Services</option>
                      <option value="admin">Administrative &amp; Business</option>
                    </select>
                  </div>
                </TableHead>
                <TableHead className="sticky top-0 z-10 bg-gray-50 hidden lg:table-cell">
                  <div className="font-semibold text-slate-600">Coverage</div>
                  <div className="mt-1 flex flex-col gap-1">
                    <input value={fState} onChange={(e) => setFState(e.target.value)} placeholder="State"
                      data-testid="filter-state" className={`w-20 rounded border px-2 py-1 text-xs font-normal ${fState ? 'border-[#0E2A47] bg-[#0E2A47]/5' : 'border-slate-200'}`} />
                    <select value={fCounty} onChange={(e) => setFCounty(e.target.value)} data-testid="filter-county"
                      className={`w-full max-w-[150px] rounded border px-1.5 py-1 text-xs font-normal ${fCounty ? 'border-[#0E2A47] bg-[#0E2A47]/5' : 'border-slate-200'}`}>
                      <option value="">All counties</option>
                      {allCounties.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </TableHead>
                <TableHead className="sticky top-0 z-10 bg-gray-50 hidden lg:table-cell">
                  <div className="font-semibold text-slate-600">Contact</div>
                  <input value={fContact} onChange={(e) => setFContact(e.target.value)} placeholder="Name / email…"
                    data-testid="filter-contact" className={`mt-1 w-full rounded border px-2 py-1 text-xs font-normal ${fContact ? 'border-[#0E2A47] bg-[#0E2A47]/5' : 'border-slate-200'}`} />
                </TableHead>
                <TableHead className="sticky top-0 z-10 bg-gray-50 hidden md:table-cell">
                  <div className="font-semibold text-slate-600">Workload</div>
                  <div className="mt-1 text-[10px] font-normal text-slate-400">(derived)</div>
                </TableHead>
                <TableHead className="sticky top-0 z-10 bg-gray-50">
                  <div className="font-semibold text-slate-600">Status</div>
                  <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} data-testid="filter-status"
                    className={`mt-1 w-full rounded border px-1.5 py-1 text-xs font-normal ${fStatus ? 'border-[#0E2A47] bg-[#0E2A47]/5' : 'border-slate-200'}`}>
                    <option value="">All</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                  <select value={fInsurance} onChange={(e) => setFInsurance(e.target.value)} data-testid="filter-insurance"
                    className={`mt-1 w-full rounded border px-1.5 py-1 text-xs font-normal ${fInsurance ? 'border-[#0E2A47] bg-[#0E2A47]/5' : 'border-slate-200'}`}>
                    <option value="">Any insurance</option>
                    <option value="expired">Ins. expired</option>
                    <option value="expiring_soon">Ins. expiring soon</option>
                    <option value="missing">Ins. date missing</option>
                  </select>
                </TableHead>
                <TableHead className="sticky top-0 z-10 bg-gray-50 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((p) => {
                const active = (p.status || 'active') === 'active';
                return (
                  <TableRow key={p.id} data-testid={`provider-row-${p.id}`}
                    onClick={() => setDetailProvider(p)}
                    tabIndex={0} role="button"
                    onKeyDown={(e) => { if (e.key === 'Enter') setDetailProvider(p); }}
                    className="cursor-pointer hover:bg-slate-50 focus:bg-slate-50 focus:outline-none">
                    <TableCell className="font-medium text-slate-800">
                      <div className="flex items-center gap-2.5">
                        <ServiceSlices provider={p} size={30} />
                        <button onClick={(e) => { e.stopPropagation(); setDetailProvider(p); }} data-testid={`provider-name-${p.id}`}
                          className="text-left font-semibold text-[#0E2A47] hover:underline">{p.company_name}</button>
                      </div>
                    </TableCell>
                    <TableCell data-testid={`provider-qsp-${p.id}`}>
                      {p.qsp_number
                        ? <span className="rounded bg-[#0E2A47] px-2 py-0.5 text-xs font-bold text-white">{p.qsp_number}</span>
                        : <span className="text-xs font-semibold italic text-amber-600">Pending</span>}
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-sm text-slate-500" data-testid={`provider-specialties-${p.id}`}>
                      {specialtyList(p).length ? specialtyList(p).join(', ') : <span className="italic text-slate-400">Not specified</span>}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell text-sm text-slate-500">{[...(p.service_states || []), ...(p.service_counties || [])].join(', ') || '—'}</TableCell>
                    <TableCell className="hidden lg:table-cell text-sm text-slate-500">{p.contact_name || '—'}</TableCell>
                    <TableCell className="hidden md:table-cell" data-testid={`provider-workload-${p.id}`}>
                      {(() => {
                        const w = insights[p.id] || { active: 0, completed: 0, next_scheduled_date: null };
                        return (
                          <div className="text-xs">
                            <div className="flex items-center gap-1.5">
                              <span className="rounded-full bg-[#EAF0F8] px-2 py-0.5 font-semibold text-[#2F6DB5]">{w.active} active</span>
                              <span className="rounded-full bg-[#EAF6DD] px-2 py-0.5 font-semibold text-[#4f8d14]">{w.completed} done</span>
                            </div>
                            <div className="mt-1 text-[11px] text-slate-500">
                              {w.next_scheduled_date ? `Next: ${_fmtDate(w.next_scheduled_date)}` : 'No upcoming job'}
                            </div>
                          </div>
                        );
                      })()}
                    </TableCell>
                    <TableCell><StatusPill status={p.status} /></TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="rounded-md p-1.5 hover:bg-gray-100" data-testid={`provider-actions-${p.id}`}>
                            <MoreHorizontal size={18} className="text-slate-500" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setDetailProvider(p)} data-testid={`provider-view-${p.id}`}>
                            <History size={14} className="mr-2" /> View card
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setFormProvider(p)} data-testid={`provider-edit-${p.id}`}>
                            <Pencil size={14} className="mr-2" /> Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setHistoryTarget(p)} data-testid={`provider-history-${p.id}`}>
                            <History size={14} className="mr-2" /> View History
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setStatusTarget({ entity: p, nextStatus: active ? 'inactive' : 'active' })}
                            data-testid={`provider-toggle-${p.id}`}>
                            <Power size={14} className="mr-2" /> {active ? 'Deactivate' : 'Reactivate'}
                          </DropdownMenuItem>
                          {canPurge && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => setPurgeTarget(p)} className="text-red-600 focus:text-red-600"
                                data-testid={`provider-purge-${p.id}`}>
                                <Trash2 size={14} className="mr-2" /> Purge
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {filtered.length === 0 && (loadError
            ? <div className="py-16 text-center text-sm text-rose-600" data-testid="provider-load-error">Unable to load service providers right now. Please retry shortly.</div>
            : <div className="py-16 text-center text-slate-400 text-sm">No service providers yet. Add your first vendor.</div>)}
        </CardContent>
      </Card>

      {/* Mobile / tablet: accessible filters + compact provider cards */}
      <div className="lg:hidden">
        <div className="mb-4 space-y-2" data-testid="provider-mobile-filters">
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input className="pl-9" placeholder="Search company, trade, contact…" value={search}
              onChange={(e) => setSearch(e.target.value)} data-testid="provider-m-search" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <select value={fQual} onChange={(e) => setFQual(e.target.value)} data-testid="provider-m-qual"
              className="min-h-[40px] rounded-md border border-slate-200 px-2 text-sm text-slate-700">
              <option value="">All qualification</option>
              <option value="qualified">Qualified</option>
              <option value="registered_pending">Registered / Pending</option>
            </select>
            <select value={fClass} onChange={(e) => setFClass(e.target.value)} data-testid="provider-m-class"
              className="min-h-[40px] rounded-md border border-slate-200 px-2 text-sm text-slate-700">
              <option value="">All classifications</option>
              <option value="field">Field Services</option>
              <option value="admin">Administrative &amp; Business</option>
            </select>
            <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} data-testid="provider-m-status"
              className="min-h-[40px] rounded-md border border-slate-200 px-2 text-sm text-slate-700">
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <select value={fInsurance} onChange={(e) => setFInsurance(e.target.value)} data-testid="provider-m-insurance"
              className="min-h-[40px] rounded-md border border-slate-200 px-2 text-sm text-slate-700">
              <option value="">Any insurance</option>
              <option value="expired">Ins. expired</option>
              <option value="expiring_soon">Ins. expiring soon</option>
              <option value="missing">Ins. date missing</option>
            </select>
          </div>
        </div>

        <MobileRecordList testid="providers-mobile-list">
          {filtered.map((p) => {
            const active = (p.status || 'active') === 'active';
            const w = insights[p.id] || { active: 0, completed: 0, next_scheduled_date: null };
            return (
              <MobileRecordCard
                key={p.id}
                testid={`provider-card-${p.id}`}
                onClick={() => setDetailProvider(p)}
                image={<ServiceSlices provider={p} size={34} />}
                title={p.company_name}
                onTitleClick={() => setDetailProvider(p)}
                titleTestid={`provider-m-name-${p.id}`}
                badge={<StatusPill status={p.status} />}
                meta={[
                  { label: 'Specialty', value: specialtyList(p).length ? specialtyList(p).join(', ') : 'Not specified', full: true },
                  { label: 'Coverage', value: [...(p.service_states || []), ...(p.service_counties || [])].join(', ') || '—', full: true },
                  { label: 'Contact', value: p.contact_name || '—' },
                  { label: 'Workload', value: `${w.active} active · ${w.completed} done` },
                ]}
                actions={(
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="rounded-md p-1.5 hover:bg-gray-100" data-testid={`provider-m-actions-${p.id}`}>
                        <MoreHorizontal size={18} className="text-slate-500" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => setDetailProvider(p)} data-testid={`provider-m-view-${p.id}`}><History size={14} className="mr-2" /> View card</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setFormProvider(p)} data-testid={`provider-m-edit-${p.id}`}><Pencil size={14} className="mr-2" /> Edit</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setHistoryTarget(p)} data-testid={`provider-m-history-${p.id}`}><History size={14} className="mr-2" /> View History</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setStatusTarget({ entity: p, nextStatus: active ? 'inactive' : 'active' })} data-testid={`provider-m-toggle-${p.id}`}><Power size={14} className="mr-2" /> {active ? 'Deactivate' : 'Reactivate'}</DropdownMenuItem>
                      {canPurge && (<><DropdownMenuSeparator /><DropdownMenuItem onClick={() => setPurgeTarget(p)} className="text-red-600 focus:text-red-600" data-testid={`provider-m-purge-${p.id}`}><Trash2 size={14} className="mr-2" /> Purge</DropdownMenuItem></>)}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              >
                {p.qsp_number
                  ? <span className="mt-2 inline-block rounded bg-[#0E2A47] px-1.5 py-0.5 text-[10px] font-bold text-white" data-testid={`provider-m-qsp-${p.id}`}>{p.qsp_number}</span>
                  : <span className="mt-2 inline-block text-[10px] font-semibold italic text-amber-600" data-testid={`provider-m-qsp-${p.id}`}>QSP Pending</span>}
              </MobileRecordCard>
            );
          })}
        </MobileRecordList>
        {filtered.length === 0 && <div className="py-16 text-center text-slate-400 text-sm">No service providers yet. Add your first vendor.</div>}
      </div>

      {formProvider !== undefined && (
        <ProviderForm provider={formProvider} onClose={() => setFormProvider(undefined)} onSaved={load} />
      )}
      {statusTarget && (
        <StatusReasonDialog kind="provider" entity={statusTarget.entity} nextStatus={statusTarget.nextStatus}
          onClose={() => setStatusTarget(null)} onDone={load} />
      )}
      {purgeTarget && (
        <PurgeEntityDialog kind="provider" entity={purgeTarget} onClose={() => setPurgeTarget(null)} onDone={load} />
      )}
      {historyTarget && (
        <HistoryDialog kind="provider" entity={historyTarget} onClose={() => setHistoryTarget(null)} />
      )}
      {detailProvider && (
        <ProviderDetailCard provider={detailProvider} onClose={() => setDetailProvider(null)}
          onEdit={(p) => { setDetailProvider(null); setFormProvider(p); }}
          onChanged={() => load()} />
      )}
      {showImport && (
        <ImportVendorsDialog onClose={() => setShowImport(false)} onDone={load} />
      )}
      {showBackfill && (
        <BackfillReviewDialog providers={items} onClose={() => setShowBackfill(false)}
          onOpenProvider={(p) => setDetailProvider(p)} onChanged={load} />
      )}
    </div>
  );
};

export default ProviderManager;
